import React, { useState, useEffect, useRef } from 'react';
import {
    View, FlatList, Text, StyleSheet, TextInput, TouchableOpacity,
    KeyboardAvoidingView, Platform, ActivityIndicator, Image, Alert, Linking, Animated
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../../../contexts/AuthContext';
import { useSocket } from '../../../contexts/SocketContext';
import { getRoomMessages, markRoomAsRead } from '../../../api/chat';
import * as ImagePicker from 'expo-image-picker';
import UserAvatar from '../../../components/social/shared/UserAvatar';
import { postData } from '../../../services/api';
import { getMediaUrl } from '../../../utils/media';
import { Video, ResizeMode } from 'expo-av';
import VideoModal from '../../../components/common/VideoModal';
import { ChatMessagesSkeleton, TypingDots } from '../../../components/shared/SkeletonLoader';
import EmojiPicker from 'rn-emoji-keyboard';

const isEmojiOnly = (str) => {
    if (!str) return false;
    const stripped = str.replace(/[\s\n]/g, '');
    if (stripped.length === 0) return false;
    // Uses Extended Pictographic to accurately detect all modern emojis
    return /^[\p{Extended_Pictographic}\uFE0F\u200D]+$/u.test(stripped);
};

const MessageItem = React.memo(({ item, isMe, isGroup, onVideoPress }) => {
    const isEmoji = isEmojiOnly(item.content) && !item.mediaUrl && item.type !== 'file';
    
    const scale = useRef(new Animated.Value(isEmoji ? 0.3 : 1)).current;

    useEffect(() => {
        if (isEmoji) {
            Animated.spring(scale, {
                toValue: 1,
                friction: 4,
                tension: 50,
                useNativeDriver: true,
            }).start();
        }
    }, [isEmoji]);

    return (
        <View style={styles.messageWrapper}>
            <View style={isMe ? styles.myMessageContainer : styles.theirMessageContainer}>
                <View style={[
                    styles.messageBubble, 
                    isMe ? styles.myMessage : styles.theirMessage,
                    isEmoji && styles.emojiOnlyBubble
                ]}>
                    {!isMe && isGroup && !isEmoji && (
                        <Text style={styles.senderName}>{item.sender?.name}</Text>
                    )}
                    {item.mediaUrl && (
                        <TouchableOpacity
                            activeOpacity={0.9}
                            onPress={() => {
                                if (item.type === 'video') {
                                    onVideoPress(getMediaUrl(item.mediaUrl));
                                }
                            }}
                            style={styles.mediaContainer}
                        >
                            {item.type === 'video' ? (
                                <View style={styles.videoPreviewContainer}>
                                    <Video
                                        source={{ uri: getMediaUrl(item.mediaUrl) }}
                                        style={styles.messageImage}
                                        resizeMode={ResizeMode.COVER}
                                        shouldPlay={false}
                                        isMuted={true}
                                    />
                                    <View style={styles.videoOverlay}>
                                        <Ionicons name="play" size={32} color="#FFFFFF" />
                                    </View>
                                </View>
                            ) : item.type === 'file' ? (
                                <TouchableOpacity
                                    style={styles.fileContainer}
                                    onPress={() => Linking.openURL(getMediaUrl(item.mediaUrl))}
                                >
                                    <Ionicons name="document-attach-outline" size={32} color="#0891b2" />
                                    <Text style={styles.fileName} numberOfLines={1}>
                                        {item.content || 'Attachment'}
                                    </Text>
                                </TouchableOpacity>
                            ) : (
                                <Image
                                    source={{ uri: getMediaUrl(item.mediaUrl) }}
                                    style={styles.messageImage}
                                    resizeMode="cover"
                                />
                            )}
                        </TouchableOpacity>
                    )}
                    {item.content ? (
                        <Animated.Text 
                            style={[
                                styles.messageText, 
                                isMe ? styles.myMessageText : styles.theirMessageText,
                                isEmoji && styles.emojiOnlyText,
                                isEmoji && { transform: [{ scale }] }
                            ]}
                        >
                            {item.content}
                        </Animated.Text>
                    ) : null}
                </View>
            </View>
            <Text style={[styles.messageTimeOutside, isMe ? styles.myTimeOutside : styles.theirTimeOutside]}>
                {new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Text>
        </View>
    );
});

const ChatRoomScreen = ({ route, navigation }) => {
    const { roomId, otherUser, room: initialRoom } = route.params;
    const [room] = useState(initialRoom);
    const isGroup = room?.type === 'group' || !!room?.name;
    const { user } = useAuth();
    const { socket, onlineUsers } = useSocket();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isTyping, setIsTyping] = useState(false);
    const [sendingMedia, setSendingMedia] = useState(false);
    const [isVideoModalVisible, setIsVideoModalVisible] = useState(false);
    const [activeVideoUrl, setActiveVideoUrl] = useState('');
    const flatListRef = useRef(null);

    const insets = useSafeAreaInsets();

    // Custom header with social theme
    useEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => (
                <View style={[styles.customHeader, { paddingTop: insets.top }]}>
                    <LinearGradient
                        colors={['#0c4a6e', '#0369a1', '#0891b2']}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={StyleSheet.absoluteFill}
                    />
                    {/* Orb decoration */}
                    <View style={styles.headerOrb} />

                    <View style={styles.headerInner}>
                        <TouchableOpacity
                            style={styles.backBtn}
                            onPress={() => navigation.goBack()}
                        >
                            <Ionicons name="arrow-back" size={22} color="rgba(255,255,255,0.9)" />
                        </TouchableOpacity>

                        <UserAvatar
                            source={isGroup ? room?.avatarUrl : otherUser?.profileImageUrl}
                            name={isGroup ? room?.name : otherUser?.name}
                            size={36}
                            showOnline={!isGroup}
                            isOnline={!isGroup && (onlineUsers?.has(otherUser?.id) || otherUser?.isOnline)}
                            icon={isGroup ? 'people' : null}
                        />

                        <View style={styles.headerTextBlock}>
                            <Text style={styles.headerName} numberOfLines={1}>
                                {isGroup ? room?.name : (otherUser?.name || 'Chat')}
                            </Text>
                            <Text style={styles.headerStatus}>
                                {isGroup
                                    ? `${room?.participants?.length || 0} participants`
                                    : (onlineUsers?.has(otherUser?.id) || otherUser?.isOnline ? '🟢 Online' : 'Offline')}
                            </Text>
                        </View>

                        <TouchableOpacity
                            style={styles.infoBtn}
                            onPress={() => {
                                if (isGroup) navigation.navigate('GroupInfo', { room });
                                else if (otherUser) navigation.navigate('UserProfile', { userId: otherUser.id });
                            }}
                        >
                            <Ionicons name="information-circle-outline" size={24} color="rgba(255,255,255,0.85)" />
                        </TouchableOpacity>
                    </View>
                </View>
            ),
        });
    }, [roomId, otherUser, isGroup, room, onlineUsers]);

    useEffect(() => {
        loadMessages();
        markRoomAsRead(roomId);

        if (socket) {
            socket.emit('join_room', roomId);

            socket.on('new_message', (data) => {
                if (data.roomId === roomId) {
                    setMessages(prev => [...prev, data.message]);
                    markRoomAsRead(roomId);
                }
            });

            socket.on('user_typing', (data) => {
                if (data.roomId === roomId && data.userId !== user?.id) {
                    setIsTyping(true);
                }
            });

            socket.on('user_stop_typing', (data) => {
                if (data.roomId === roomId && data.userId !== user?.id) {
                    setIsTyping(false);
                }
            });

            return () => {
                socket.off('new_message');
                socket.off('user_typing');
                socket.off('user_stop_typing');
            };
        }
    }, [roomId, socket]);

    const loadMessages = async () => {
        try {
            const data = await getRoomMessages(roomId);
            setMessages(data);
        } catch (error) {
            console.error('Failed to load messages:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // FIXED: Separate text send from media send — no parameter confusion
    const handleSendText = () => {
        const text = input.trim();
        if (!text || !socket) return;

        socket.emit('send_message', {
            roomId,
            content: text,
            type: 'text',
            mediaUrl: null,
            mediaType: null,
        });

        setInput('');
        socket.emit('typing_stop', roomId);
    };

    const handleSendMedia = (mediaData) => {
        if (!socket || !mediaData) return;

        socket.emit('send_message', {
            roomId,
            content: '',
            type: mediaData.mediaType?.startsWith('video') ? 'video' : 'image',
            mediaUrl: mediaData.mediaUrl,
            mediaType: mediaData.mediaType,
        });

        socket.emit('typing_stop', roomId);
    };

    const pickMedia = async () => {
        // Fix: Use MediaTypeOptions instead of MediaType to support modern Expo
        // and add allowsMultipleSelection to enable selecting multiple media files
        try {
            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ImagePicker.MediaTypeOptions.All,
                allowsMultipleSelection: true,
                quality: 0.7,
            });

            if (!result.canceled) {
                setSendingMedia(true);
                // Handle multiple assets being selected
                for (const asset of result.assets) {
                    try {
                        const formData = new FormData();
                        if (Platform.OS === 'web') {
                            const response = await fetch(asset.uri);
                            const blob = await response.blob();
                            const extension = asset.type === 'video' ? 'mp4' : 'jpg';
                            formData.append('media', blob, `chat_${Date.now()}.${extension}`);
                        } else {
                            // Extract file extension and determine correct MIME type
                            const uriParts = asset.uri.split('.');
                            const extension = uriParts[uriParts.length - 1].toLowerCase() || (asset.type === 'video' ? 'mp4' : 'jpg');

                            // Determine correct MIME type
                            const mimeType = asset.type === 'video'
                                ? `video/${extension === 'mov' ? 'quicktime' : 'mp4'}`
                                : `image/${extension === 'png' ? 'png' : 'jpeg'}`;

                            formData.append('media', {
                                uri: asset.uri,
                                name: `chat_${Date.now()}.${extension}`,
                                type: mimeType,
                            });
                        }

                        const response = await postData(`/api/chat/rooms/${roomId}/media`, formData);
                        handleSendMedia(response);
                    } catch (error) {
                        console.error('Individual asset upload error:', error);
                    }
                }
            }
        } catch (error) {
            console.error('Media picker error:', error);
            Alert.alert('Error', 'Could not open media library or select files.');
        } finally {
            setSendingMedia(false);
        }
    };

    const handleInputChange = (text) => {
        setInput(text);
        if (socket) {
            if (text.length > 0) socket.emit('typing_start', roomId);
            else socket.emit('typing_stop', roomId);
        }
    };

    const renderMessage = ({ item }) => {
        const isMe = item.senderId === user?.id;
        
        return (
            <MessageItem 
                item={item} 
                isMe={isMe} 
                isGroup={isGroup} 
                onVideoPress={(url) => {
                    setActiveVideoUrl(url);
                    setIsVideoModalVisible(true);
                }}
            />
        );
    };

    return (
        <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
            style={styles.container}
        >
            {isLoading ? (
                <ChatMessagesSkeleton />
            ) : (
                <FlatList
                    ref={flatListRef}
                    data={messages}
                    renderItem={renderMessage}
                    keyExtractor={item => item.id?.toString() || Math.random().toString()}
                    contentContainerStyle={styles.messageList}
                    onContentSizeChange={() => flatListRef.current?.scrollToEnd({ animated: true })}
                    showsVerticalScrollIndicator={false}
                />
            )}

            {isTyping && <TypingDots />}

            <View style={styles.inputContainer}>
                <TouchableOpacity style={styles.attachmentButton} onPress={pickMedia} disabled={sendingMedia}>
                    {sendingMedia ? (
                        <ActivityIndicator size="small" color="#06b6d4" />
                    ) : (
                        <Ionicons name="add" size={24} color="#06b6d4" />
                    )}
                </TouchableOpacity>

                <TouchableOpacity style={styles.attachmentButton} onPress={() => setIsEmojiPickerOpen(true)}>
                    <Ionicons name="happy-outline" size={24} color="#06b6d4" />
                </TouchableOpacity>

                <TextInput
                    style={styles.input}
                    placeholder="Type a message..."
                    placeholderTextColor="#9CA3AF"
                    value={input}
                    onChangeText={handleInputChange}
                    multiline
                    returnKeyType="default"
                />

                <TouchableOpacity
                    style={[styles.sendButton, !input.trim() && styles.sendButtonDisabled]}
                    onPress={handleSendText}
                    disabled={!input.trim()}
                >
                    <Ionicons name="send" size={18} color="#FFFFFF" />
                </TouchableOpacity>
            </View>

            <VideoModal
                isVisible={isVideoModalVisible}
                videoUrl={activeVideoUrl}
                onClose={() => setIsVideoModalVisible(false)}
            />

            <EmojiPicker
                onEmojiSelected={(emojiObject) => handleInputChange(input + emojiObject.emoji)}
                open={isEmojiPickerOpen}
                onClose={() => setIsEmojiPickerOpen(false)}
                enableSearchBar={true}
                enableRecentlyUsed={true}
            />
        </KeyboardAvoidingView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F0FDFF',
    },

    // Custom header
    customHeader: {
        overflow: 'hidden',
        paddingBottom: 12,
    },
    headerOrb: {
        position: 'absolute',
        width: 100,
        height: 100,
        borderRadius: 50,
        backgroundColor: 'rgba(34,211,238,0.1)',
        right: -20,
        top: -30,
    },
    headerInner: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 14,
        paddingTop: Platform.OS === 'ios' ? 8 : 14,
        gap: 10,
    },
    backBtn: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: 'rgba(255,255,255,0.12)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerTextBlock: {
        flex: 1,
    },
    headerName: {
        fontSize: 16,
        fontWeight: '700',
        color: '#FFFFFF',
    },
    headerStatus: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.65)',
        marginTop: 1,
    },
    infoBtn: {
        padding: 4,
    },

    // Messages
    messageList: {
        padding: 14,
        paddingBottom: 20,
    },
    messageWrapper: {
        marginBottom: 4,
    },
    myMessageContainer: {
        alignItems: 'flex-end',
    },
    theirMessageContainer: {
        alignItems: 'flex-start',
    },
    messageBubble: {
        maxWidth: '78%',
        padding: 11,
        borderRadius: 16,
    },
    myMessage: {
        backgroundColor: '#0891b2',
        borderBottomRightRadius: 3,
    },
    theirMessage: {
        backgroundColor: '#FFFFFF',
        borderBottomLeftRadius: 3,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 2,
        elevation: 1,
    },
    emojiOnlyBubble: {
        backgroundColor: 'transparent',
        shadowOpacity: 0,
        elevation: 0,
        padding: 0,
    },
    messageText: {
        fontSize: 15,
        lineHeight: 20,
    },
    emojiOnlyText: {
        fontSize: 48,
        lineHeight: 56,
        includeFontPadding: false,
    },
    myMessageText: {
        color: '#FFFFFF',
    },
    theirMessageText: {
        color: '#111827',
    },
    messageImage: {
        width: 220,
        height: 160,
        borderRadius: 10,
        marginBottom: 5,
    },
    mediaContainer: {
        width: 220,
        height: 160,
        borderRadius: 10,
        overflow: 'hidden',
        marginBottom: 5,
        backgroundColor: '#000',
    },
    videoPreviewContainer: {
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
    },
    videoOverlay: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(0,0,0,0.3)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    fileContainer: {
        width: 180,
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F3F4F6',
        padding: 12,
        borderRadius: 12,
        gap: 10,
    },
    fileName: {
        flex: 1,
        fontSize: 13,
        color: '#374151',
        fontWeight: '600',
    },
    messageTimeOutside: {
        fontSize: 10,
        color: '#9CA3AF',
        marginTop: 2,
        marginBottom: 6,
    },
    myTimeOutside: {
        alignSelf: 'flex-end',
        marginRight: 4,
    },
    theirTimeOutside: {
        alignSelf: 'flex-start',
        marginLeft: 4,
    },
    senderName: {
        fontSize: 11,
        fontWeight: '700',
        color: '#0891b2',
        marginBottom: 3,
    },
    typingText: {
        paddingHorizontal: 20,
        paddingVertical: 4,
        fontSize: 12,
        color: '#9CA3AF',
        fontStyle: 'italic',
    },

    // Input bar
    inputContainer: {
        flexDirection: 'row',
        padding: 10,
        paddingBottom: Platform.OS === 'ios' ? 28 : 12,
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        borderTopWidth: 1,
        borderTopColor: 'rgba(6,182,212,0.12)',
        gap: 8,
    },
    attachmentButton: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: 'rgba(6,182,212,0.08)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    input: {
        flex: 1,
        backgroundColor: '#F0FDFF',
        borderRadius: 20,
        borderWidth: 1,
        borderColor: 'rgba(6,182,212,0.2)',
        paddingHorizontal: 14,
        paddingVertical: 8,
        fontSize: 15,
        color: '#111827',
        maxHeight: 100,
    },
    sendButton: {
        backgroundColor: '#0891b2',
        width: 38,
        height: 38,
        borderRadius: 19,
        alignItems: 'center',
        justifyContent: 'center',
    },
    sendButtonDisabled: {
        backgroundColor: '#A5F3FC',
    },
});

export default ChatRoomScreen;
